import axios, { AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import Layout from "../../components/layout";
import useAppDispatch from "../../hooks/useDispatch";
import useAppSelector from "../../hooks/useSelector";
import {
  setFromCoinSelectData,
  setToCoinSelectData,
} from "../../redux/slices/coinSelectSlice";
import {
  setExchangeResData,
  setRateId,
} from "../../redux/slices/exchangeResSlice";
import { setSelectedExchangeData } from "../../redux/slices/selectedExchangeSlice";
import Swap from "./swap";
import Tokens from "./tokens";
import { baseUrl } from "../../config/url";
import {
  setNetIconFromData,
  setNetIconToData,
} from "../../redux/slices/netIconSlice";
import History from "./history";
import net from "../../data/network.json";
import { useMutation } from "@tanstack/react-query";
import { priceService } from "../../services/price";
import {
  setFromWalletAddressData,
  setToWalletAddressData,
  setValidAddressFromData,
  setValidAddressToData,
} from "../../redux/slices/walletAddress";
import {
  setExtraIdFromData,
  setExtraIdToData,
} from "../../redux/slices/extraIdSlice";
import {
  setNetMemoFromData,
  setNetMemoToData,
} from "../../redux/slices/netMemoSlice";
import { debounce } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { setFromValueData } from "../../redux/slices/coinFromValueSlice";
import coins from "../../data/coinList.json";
import { baseImageUrl } from "../../config/imageUrl";

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [swith, setSwitch] = useState(1);
  const [coinType, setCoinType] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [swapDetail, setSwapDetail] = useState(false);
  const [active, setActive] = useState<number>(0);
  const controllerRef = useRef<AbortController | null>(null);
  const dispatch = useAppDispatch();
  const { coinData } = useAppSelector((state) => state.coinSelect);
  const { netIconFrom } = useAppSelector((state) => state.netIcon);
  const { netIconTo } = useAppSelector((state) => state.netIcon);
  const [showWallet, setShowWallet] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [historyRequestId, setHistoryRequestId] = useState<string>("");
  const [price, setPrice] = useState<any>();
  const activeRef = useRef(active);

  const fromValueData = useAppSelector(
    (state) => state.coinFromValue.fromValue
  );

  const toWalletAddressData = useAppSelector(
    (state) => state.walletAddress.toWalletAddress
  );

  const fromWalletAddressData = useAppSelector(
    (state) => state.walletAddress.fromWalletAddress
  );

  const validAddressToData = useAppSelector(
    (state) => state.walletAddress.validAddressTo
  );

  const validAddressFromData = useAppSelector(
    (state) => state.walletAddress.validAddressFrom
  );

  const selectedExchangeData = useAppSelector(
    (state) => state.selectedExchange
  );

  const exchangeResData = useAppSelector((state) => state.exchangeRes);

  const fromExtraData = useAppSelector((state) => state.extraId.extraIdFrom);
  const toExtraData = useAppSelector((state) => state.extraId.extraIdTo);
  const fromMemoData = useAppSelector((state) => state.netMemo.netMemoFrom);
  const toMemoData = useAppSelector((state) => state.netMemo.netMemoTo);
  const [errorMessage, setErrorMessage] = useState(false);
  const [loadingSkeleton, setLoadingSkeleton] = useState(false);
  const [timer, setTimer] = useState<number>(0);
  const priceQuery = useMutation({
    mutationFn: priceService,
  });

  const handleChange = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("symbol_from", coinData[1]?.symbol);
    searchParams.set("symbol_to", coinData[0]?.symbol);

    const networkFrom =
      net.find((item) => item?.name === coinData[1]?.network)?.name || "";
    const networkTo =
      net.find((item) => item?.name === coinData[0]?.network)?.name || "";

    searchParams.set("network_from", networkFrom);
    searchParams.set("network_to", networkTo);
    let toValue = "0";
    if (selectedExchangeData?.selectedExchange?.amount) {
      toValue = selectedExchangeData.selectedExchange.amount.toFixed(5);
    } else if (exchangeResData?.exchangeRes) {
      toValue = Number(exchangeResData?.exchangeRes).toFixed(5);
    } else if (fromValueData) {
      toValue = String(fromValueData);
    }

    if (!isNaN(Number(toValue))) {
      searchParams.set("amount", toValue);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });

    dispatch(setToCoinSelectData(coinData[0]));
    dispatch(setFromCoinSelectData(coinData[1]));

    dispatch(setNetIconFromData(netIconTo));
    dispatch(setNetIconToData(netIconFrom));

    dispatch(setToWalletAddressData(fromWalletAddressData));
    dispatch(setFromWalletAddressData(toWalletAddressData));

    dispatch(setValidAddressToData(validAddressFromData));
    dispatch(setValidAddressFromData(validAddressToData));

    dispatch(setExtraIdFromData(toExtraData));
    dispatch(setExtraIdToData(fromExtraData));

    dispatch(setNetMemoFromData(toMemoData));
    dispatch(setNetMemoToData(fromMemoData));

    dispatch(setFromValueData(toValue));

    if (!fromValueData || isNaN(Number(fromValueData))) {
      dispatch(setFromValueData(""));
      return;
    }
    setLoading(true);
    setActive(0);
    dispatch(setExchangeResData());
    dispatch(setRateId());
    dispatch(setSelectedExchangeData());

    priceQuery.mutate(coinData?.[0]?.uuid, {
      onSuccess: (data) => {
        const currentPrice = data?.data?.currentPrice;

        if (typeof currentPrice === "string") {
          const numericPrice = parseFloat(currentPrice);
          if (!isNaN(numericPrice)) {
            setPrice(numericPrice.toFixed(2));
          } else {
            console.error("Error: currentPrice is not a valid number.");
          }
        } else {
          console.error("Error: currentPrice is not a string.");
        }
      },
      onError: (error) => {
        console.error("Error:", error);
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 403) {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      },
    });
  };

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const fullExchange = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    controllerRef.current = new AbortController();
    setLoadingSkeleton(true);
    setErrorMessage(false);

    const searchParams = new URLSearchParams(location.search);
    const search_symbol_from = searchParams.get("symbol_from");
    const search_network_from = searchParams.get("network_from");
    const search_symbol_to = searchParams.get("symbol_to");
    const search_network_to = searchParams.get("network_to");
    const search_amount = searchParams.get("amount");
    if (
      search_symbol_from &&
      search_symbol_to &&
      search_network_from &&
      search_network_to &&
      search_amount
    ) {
      dispatch(
        setFromCoinSelectData(
          coins.filter(
            (item) =>
              item?.symbol.toUpperCase() === search_symbol_from.toUpperCase() &&
              item?.network.toUpperCase() === search_network_from.toUpperCase()
          )?.[0]
        )
      );
      dispatch(
        setToCoinSelectData(
          coins.filter(
            (item) =>
              item?.symbol.toUpperCase() === search_symbol_to.toUpperCase() &&
              item?.network.toUpperCase() === search_network_to.toUpperCase()
          )?.[0]
        )
      );
      dispatch(setFromValueData(search_amount));
    }

    if (fromValueData && Number(fromValueData) > 0) {
      const params = {
        symbol_from: search_symbol_from
          ? search_symbol_from
          : coinData[0]?.symbol,
        network_from: search_network_from
          ? search_network_from
          : netIconFrom
          ? net.filter(
              (item) =>
                (item.image.startsWith("http")
                  ? item.image
                  : `${baseImageUrl}/${item.image}`) === netIconFrom
            )?.[0]?.name
          : net.filter(
              (item) =>
                item?.name.toUpperCase() === coinData[0]?.network.toUpperCase()
            )?.[0]?.name,
        symbol_to: search_symbol_to ? search_symbol_to : coinData[1]?.symbol,
        network_to: search_network_to
          ? search_network_to
          : netIconTo
          ? net.filter(
              (item) =>
                (item.image.startsWith("http")
                  ? item.image
                  : `${baseImageUrl}/${item.image}`) === netIconTo
            )?.[0]?.name
          : net.filter(
              (item) =>
                item?.name.toUpperCase() === coinData[1]?.network.toUpperCase()
            )?.[0]?.name,
        amount: fromValueData || search_amount || "",
      };
      axios
        .get(`${baseUrl}FullEstimatedExchangeAmount`, {
          withCredentials: true,
          signal: controllerRef.current.signal,
          params,
        })
        .then((response) => {
          if (activeRef.current === 0) {
            dispatch(setExchangeResData(response?.data));
          }
          dispatch(setRateId(response?.data?.rate_id));
          dispatch(
            setSelectedExchangeData(response?.data?.data[activeRef.current])
          );
          setLoading(false);
          setLoadingSkeleton(false);
          if (response?.data?.message !== "OK") {
            setErrorMessage(true);
          }
        })
        .catch((error) => {
          setLoadingSkeleton(false);
          if (axios.isCancel(error)) {
            console.log("Previous request canceled");
          } else if (error.name === "CanceledError") {
            console.log("Request was aborted");
          } else {
            console.error("Error:", error);
            if (error.response?.status === 403) {
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            }
            setLoading(true);
            dispatch(setExchangeResData());
            dispatch(setRateId());
          }
        });
    }
  }, [fromValueData, coinData]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const search_symbol_from = searchParams.get("symbol_from");
    const search_network_from = searchParams.get("network_from");
    const search_symbol_to = searchParams.get("symbol_to");
    const search_network_to = searchParams.get("network_to");
    const search_amount = searchParams.get("amount");

    const fromCoin = coins.find(
      (item) =>
        item.symbol.toLowerCase() === search_symbol_from?.toLowerCase() &&
        item.network.toLowerCase() === search_network_from?.toLowerCase()
    );

    const toCoin = coins.find(
      (item) =>
        item.symbol.toLowerCase() === search_symbol_to?.toLowerCase() &&
        item.network.toLowerCase() === search_network_to?.toLowerCase()
    );

    if (fromCoin) {
      dispatch(setFromCoinSelectData(fromCoin));
    }

    if (toCoin) {
      dispatch(setToCoinSelectData(toCoin));
    }

    if (search_amount) {
      dispatch(setFromValueData(search_amount));
    }
  }, [location.search, coins]);

  const startTimer = useCallback(() => {
    let currentProgress = 0;
    setTimer(0);
    setLoadingSkeleton(true);
    const progressInterval = setInterval(() => {
      currentProgress += 100 / 60;
      setTimer(currentProgress);

      if (currentProgress >= 100) {
        clearInterval(progressInterval);
        setLoadingSkeleton(true);
      }
    }, 1000);

    return progressInterval;
  }, []);

  const debouncedFullExchange = useCallback(
    debounce(() => {
      fullExchange();
    }, 700),
    [fullExchange]
  );

  useEffect(() => {
    let progressInterval = startTimer();
    const intervalId = setInterval(() => {
      clearInterval(progressInterval);
      progressInterval = startTimer();
      debouncedFullExchange();
    }, 60000);

    if (!swapDetail) {
      debouncedFullExchange();
    } else {
      clearInterval(intervalId);
      setTimer(0);
    }
    return () => {
      clearInterval(intervalId);
      clearInterval(progressInterval);
      setTimer(0);
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      debouncedFullExchange.cancel();
    };
  }, [debouncedFullExchange, swapDetail, startTimer]);

  const handlePrice = useCallback(
    debounce(() => {
      if (fromValueData && Number(fromValueData) > 0) {
        priceQuery.mutate(coinData?.[1]?.uuid, {
          onSuccess: (data) => {
            const currentPrice = data?.data?.currentPrice;
            if (typeof currentPrice === "string") {
              const numericPrice = parseFloat(currentPrice);
              if (!isNaN(numericPrice)) {
                setPrice(numericPrice.toFixed(2));
              } else {
                console.error(
                  "Error: currentPrice cannot be converted to a valid number."
                );
              }
            } else {
              console.error("Error: currentPrice is not a string.");
            }
          },
          onError: (error) => {
            console.error("Error:", error);
            const axiosError = error as AxiosError;
            if (axiosError.response?.status === 403) {
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            }
          },
        });
      }
    }, 700),
    [fromValueData, priceQuery, coinData]
  );

  useEffect(() => {
    handlePrice();
    return () => {
      handlePrice.cancel();
    };
  }, [fromValueData]);

  return (
    <Layout>
      {swith === 1 && (
        <Swap
          setSwitch={setSwitch}
          setCoinType={setCoinType}
          loading={loading}
          setLoading={setLoading}
          swapDetail={swapDetail}
          setSwapDetail={setSwapDetail}
          handleChange={handleChange}
          setActive={setActive}
          active={active}
          showWallet={showWallet}
          setShowWallet={setShowWallet}
          confirm={confirm}
          setConfirm={setConfirm}
          historyRequestId={historyRequestId}
          setHistoryRequestId={setHistoryRequestId}
          price={price}
          setPrice={setPrice}
          timer={timer}
          loadingSkeleton={loadingSkeleton}
          errorMessage={errorMessage}
        />
      )}
      {swith === 2 && (
        <Tokens
          setActive={setActive}
          setSwitch={setSwitch}
          coinType={coinType}
          setLoading={setLoading}
          setPrice={setPrice}
        />
      )}
      {swith === 3 && (
        <History
          setSwitch={setSwitch}
          swapDetail={swapDetail}
          setSwapDetail={setSwapDetail}
          setConfirm={setConfirm}
          setShowWallet={setShowWallet}
          historyRequestId={historyRequestId}
          setHistoryRequestId={setHistoryRequestId}
          setLoading={setLoading}
          setActive={setActive}
        />
      )}
    </Layout>
  );
};
export default Home;
